import { applyResolvedUrlsToDoc, collectDocImageSrcs, isDocEmpty } from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import type { VersionChanges } from '../../../services/productVersionPlan';
import type {
    ProductRow,
    ProductVersion,
    UpdateProductBody,
    VersionBody
} from '../../../services/billing';

// The category whose products the storefront sells (mirrors
// services/products/taxonomy.ts). A UI affordance; the server enforces sale.
export const WEBSTORE_CATEGORY = 'Webstore';

export interface FormState {
    name: string;
    description: string;
    category: string;
    subCategory: string;
    // products.vendor.id. '' = none. Ordering methods are picked per version.
    vendorId: string;
    // yyyy-mm-dd ('' = no expiry). Past this date the product is retired:
    // hidden from charge/pricing pickers, kept for history.
    expiresAt: string;
}

export const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

export interface MerchandisingState {
    // The hero image as it stands after any pick or removal; a newly picked
    // File has no id until it is uploaded on submit.
    imageFileId: string | null;
    marketingCopyDoc: TiptapDoc;
    // Storefront gate: gated in the UI on the product being ready to sell.
    sellableInWebstore: boolean;
}

export interface VersionFormRow {
    _key: number;
    productVersionId: string | null; // null = new version
    label: string;
    selectors: Record<string, string>;
    price: string;
    // '' = null (not tracked) - unlike price, vendor cost is optional/clearable.
    vendorCost: string;
    billId: string;
    // Vendor part number (webstore); '' = null. Distinct from billId.
    vendorPartNumber: string;
    // Units per sellable box (multiplier, not a quantity); '' = null.
    packSize: string;
    // '' = none (no purchase order raised when this version is charged).
    orderMethodId: string;
    // yyyy-mm-dd, the UTC day the version expires on; '' = no expiry.
    expiresAt: string;
    isDefault: boolean;
}

let _versionKey = 0;
function nextKey() {
    return ++_versionKey;
}

export function toFormState(product: ProductRow | null): FormState {
    return {
        name: product?.name ?? '',
        description: product?.description ?? '',
        category: product?.category ?? '',
        subCategory: product?.subCategory ?? '',
        vendorId: product?.vendorId ?? '',
        expiresAt: product?.expiresAt ? product.expiresAt.slice(0, 10) : ''
    };
}

export function toMerchandisingState(product: ProductRow | null): MerchandisingState {
    return {
        imageFileId: product?.imageFileId ?? null,
        marketingCopyDoc: product?.marketingCopyDoc ?? EMPTY_DOC,
        sellableInWebstore: product?.sellableInWebstore ?? false
    };
}

// The editor is fed URLs resolved on open; those expire, so the stored `src`
// is put back before saving and only newly uploaded images keep a fresh one.
export function marketingCopyDocForSave(
    product: ProductRow | null,
    doc: TiptapDoc
): TiptapDoc | null {
    if (isDocEmpty(doc)) return null;
    if (!product?.marketingCopyDoc) return doc;
    return applyResolvedUrlsToDoc(doc, collectDocImageSrcs(product.marketingCopyDoc));
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

// `src` is the only attribute the resolve step rewrites, so it is the one
// difference that does not mean the user edited anything.
function normalizeForDiff(value: unknown, insideAttrs = false): unknown {
    if (Array.isArray(value)) return value.map((item) => normalizeForDiff(item));
    if (!isRecord(value)) return value;
    return Object.fromEntries(
        Object.keys(value)
            .filter((key) => !(insideAttrs && key === 'src'))
            .sort()
            .map((key) => [key, normalizeForDiff(value[key], key === 'attrs')])
    );
}

function docsEqual(a: TiptapDoc | null, b: TiptapDoc | null): boolean {
    if (!a || !b) return a === b;
    return JSON.stringify(normalizeForDiff(a)) === JSON.stringify(normalizeForDiff(b));
}

function toDateInput(iso: string | null): string {
    return iso ? iso.slice(0, 10) : '';
}

export function toVersionRows(versions: ProductVersion[]): VersionFormRow[] {
    return versions.map((version) => ({
        _key: nextKey(),
        productVersionId: version.id,
        label: version.label,
        selectors: { ...version.selectors },
        price: (version.priceCents / 100).toFixed(2),
        vendorCost:
            version.vendorCostCents !== null ? (version.vendorCostCents / 100).toFixed(2) : '',
        billId: version.billId ?? '',
        vendorPartNumber: version.vendorPartNumber ?? '',
        packSize: version.packSize != null ? String(version.packSize) : '',
        orderMethodId: version.orderMethod?.id ?? '',
        expiresAt: toDateInput(version.expiresAt),
        isDefault: version.isDefault
    }));
}

export function emptyVersionRow(label = ''): VersionFormRow {
    return {
        _key: nextKey(),
        productVersionId: null,
        label,
        selectors: {},
        price: '0.00',
        vendorCost: '',
        billId: '',
        vendorPartNumber: '',
        packSize: '',
        orderMethodId: '',
        expiresAt: '',
        isDefault: false
    };
}

// The storefront renders an image, the copy and a vendor part number, so a
// product missing any of them cannot be offered for sale.
export function webstoreReadinessGaps(
    hasImage: boolean,
    copyDoc: TiptapDoc,
    versionRows: VersionFormRow[]
): string[] {
    const gaps: string[] = [];
    if (!hasImage) gaps.push('a product image');
    if (isDocEmpty(copyDoc)) gaps.push('marketing copy');
    if (!versionRows.some((row) => row.vendorPartNumber.trim())) gaps.push('a vendor part number');
    return gaps;
}

function isNonNegativeNumber(value: string): boolean {
    return value !== '' && !isNaN(Number(value)) && Number(value) >= 0;
}

export interface WebstoreValidationState {
    sellableInWebstore: boolean;
    readinessGaps: string[];
}

// The first thing wrong with the form, phrased for a toast; null = submittable.
export function validateProductForm(
    form: FormState,
    versionRows: VersionFormRow[],
    webstore: WebstoreValidationState
): string | null {
    if (!form.name.trim()) return 'Name is required';
    if (versionRows.some((row) => !row.label.trim())) return 'Every version needs a label';
    if (versionRows.some((row) => !isNonNegativeNumber(row.price)))
        return 'Enter a valid price for every version';
    if (
        versionRows.some(
            (row) => row.vendorCost.trim() !== '' && !isNonNegativeNumber(row.vendorCost.trim())
        )
    )
        return 'Enter a valid vendor cost or leave it blank';
    if (
        versionRows.some(
            (row) =>
                row.packSize.trim() !== '' &&
                (!Number.isInteger(Number(row.packSize)) || Number(row.packSize) < 1)
        )
    )
        return 'Pack size must be a whole number of units, or blank';

    const labels = versionRows.map((row) => row.label.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) return 'Version labels must be unique';

    // The server rejects such a save part-way through the version writes, which
    // would leave the ones already applied committed.
    if (webstore.sellableInWebstore && webstore.readinessGaps.length > 0)
        return `A product sellable in the webstore needs ${webstore.readinessGaps.join(', ')}`;
    return null;
}

// Empty string on a nullable field means "clear it" (sent as null).
export function toNullable(value: string): string | null {
    return value.trim() || null;
}

export function buildUpdateBody(
    product: ProductRow,
    form: FormState,
    merchandising: MerchandisingState
): UpdateProductBody {
    const body: UpdateProductBody = {};

    const name = form.name.trim();
    if (name !== product.name) body.name = name;

    if (toNullable(form.description) !== product.description)
        body.description = toNullable(form.description);
    if (toNullable(form.category) !== product.category) body.category = toNullable(form.category);
    if (toNullable(form.subCategory) !== product.subCategory)
        body.subCategory = toNullable(form.subCategory);

    const vendorId = toNullable(form.vendorId);
    if (vendorId !== product.vendorId) body.vendorId = vendorId;

    const currentExpiry = product.expiresAt ? product.expiresAt.slice(0, 10) : null;
    if ((form.expiresAt || null) !== currentExpiry) body.expiresAt = form.expiresAt || null;

    if (merchandising.imageFileId !== product.imageFileId)
        body.imageFileId = merchandising.imageFileId;

    const copyDoc = marketingCopyDocForSave(product, merchandising.marketingCopyDoc);
    if (!docsEqual(copyDoc, product.marketingCopyDoc)) body.marketingCopyDoc = copyDoc;

    if (merchandising.sellableInWebstore !== product.sellableInWebstore)
        body.sellableInWebstore = merchandising.sellableInWebstore;

    return body;
}

export function toVersionBody(row: VersionFormRow): VersionBody {
    return {
        label: row.label.trim(),
        selectors: { ...row.selectors },
        priceCents: Math.round(Number(row.price) * 100),
        vendorCostCents:
            row.vendorCost.trim() === '' ? null : Math.round(Number(row.vendorCost) * 100),
        billId: toNullable(row.billId),
        vendorPartNumber: toNullable(row.vendorPartNumber),
        packSize: row.packSize.trim() === '' ? null : Math.round(Number(row.packSize)),
        orderMethodId: toNullable(row.orderMethodId),
        expiresAt: row.expiresAt ? `${row.expiresAt}T00:00:00.000Z` : null,
        isDefault: row.isDefault
    };
}

function sameSelectors(a: Record<string, string>, b: Record<string, string>): boolean {
    const aKeys = Object.keys(a);
    return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
}

export function buildVersionChanges(
    original: ProductVersion[],
    rows: VersionFormRow[]
): VersionChanges {
    const originalById = new Map(original.map((version) => [version.id, version]));
    const keptIds = new Set(rows.map((row) => row.productVersionId).filter(Boolean));

    const changes: VersionChanges = { create: [], update: [], delete: [] };

    for (const row of rows) {
        const body = toVersionBody(row);
        if (row.productVersionId === null) {
            changes.create.push(body);
            continue;
        }
        const existing = originalById.get(row.productVersionId);
        if (!existing) continue;
        const changed =
            body.label !== existing.label ||
            !sameSelectors(body.selectors, existing.selectors) ||
            body.priceCents !== existing.priceCents ||
            body.vendorCostCents !== existing.vendorCostCents ||
            body.billId !== existing.billId ||
            body.vendorPartNumber !== existing.vendorPartNumber ||
            (body.packSize ?? null) !== (existing.packSize ?? null) ||
            body.orderMethodId !== (existing.orderMethod?.id ?? null) ||
            row.expiresAt !== toDateInput(existing.expiresAt) ||
            row.isDefault !== existing.isDefault;
        if (changed)
            changes.update.push({
                productVersionId: row.productVersionId,
                currentLabel: existing.label,
                body
            });
    }

    changes.delete = original
        .filter((version) => !keptIds.has(version.id))
        .map((version) => ({ productVersionId: version.id, currentLabel: version.label }));

    return changes;
}
